import { Suspense, useRef } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { Image } from '@/routes/-components/image';
import { trpc } from '@/lib/trpc';

type SuccessCase = {
	company: string;
	/** The merchant's donor portal — where the logo on the tab comes from. */
	portalId: string;
	projects: string;
	raised: string;
	quote: string;
	author?: { name: string; role: string };
};

/**
 * The four merchants this deck tells a story about, in the order it tells them. Exported so the route
 * can warm their logos in its loader: the deck itself is written by hand (see `SuccessCases`), and
 * these are the only ids on the page that need a query.
 */
export const SUCCESS_CASE_PORTAL_IDS = ['telefonica', 'ticketmaster', 'eroski', 'correos'] as const;

/** The tab's inner width, and so the width every logo is laid out at. */
const LOGO_WIDTH = 132;

/**
 * The merchant's OWN logo, read from its donor portal (`portal.getAuthor` → `logo`), which is the same
 * artwork the partner wheel above renders and the merchant maintains in the backoffice.
 *
 * It replaces four files under `/home/partners`: a 125x55 canvas with the mark inside it at about half
 * that, so half of every logo drawn here was the file's own margin — which is why the review read them
 * as stamps floating in the tab, twice. The records hold vectors, tight to their own ink.
 *
 * The record's `width`/`height` is what the box is laid out at, so each logo keeps its OWN proportions
 * and nothing letterboxes; `contain` still guards the odd record whose numbers disagree with its file.
 */
function CaseLogo({ portalId, company }: { portalId: string; company: string }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const { data } = useSuspenseQuery(trpc.portal.getAuthor.queryOptions({ portalId, locale }));
	const logo = 'notFound' in data ? undefined : data.logo;
	if (!logo) return null;

	// A record may carry a logo whose dimensions were never read (they are optional upstream). 2:1 is
	// the proportion every other logo box on the site normalises to — see `partner-logo.tsx`.
	const aspect = logo.width && logo.height ? logo.width / logo.height : 2;
	return (
		<Image
			src={logo.location}
			alt={company}
			height={Math.round(LOGO_WIDTH / aspect)}
			aspectRatio={aspect}
			objectFit="contain"
			className="max-h-15 w-full object-contain! mix-blend-multiply contrast-200 grayscale"
		/>
	);
}

function Figure(props: { value: string; label: string }) {
	return (
		<div className="text-right">
			<p className="text-4xl leading-[1.1] font-medium tracking-[-0.02em] tabular-nums md:text-5xl">{props.value}</p>
			{/* No gap and no size bump AT `md`: the hand-off's frame is exactly 74px tall — 53 for the number
			    at 48/110% plus 21 for the label at 16/130% — so any margin there overflows it. Below `md` the
			    label steps down because the space left of the inset is what the fixed 161px notch leaves:
			    79px on a 320 viewport, which "financiados" does not fit at 16px. */}
			<p className="text-ink/50 text-sm leading-[1.3] tracking-[-0.02em] md:text-base">{props.label}</p>
		</div>
	);
}

/**
 * A partner story. The hand-off's card is 586x398 with the brand mark on a cream tab that the card
 * curves AROUND — measuring the export shows the channel between tab and card is transparent, so the
 * card genuinely has a concave bite taken out of its top-left, rather than the tab sitting on top of it.
 *
 * That bite is cut by `NOTCH`: a block painted in the section's own background whose rounded
 * bottom-right corner reads, from the card's side, as a concave one. It is the cheapest way to get a
 * true inverted radius — a CSS mask would need a hand-tuned gradient stack for the same result.
 *
 * The trade-off is that the block hardcodes the surface behind it. This section is `bg-white`; drop the
 * card on any other background and the notch turns into a white smear.
 */
function CaseCard(props: { item: SuccessCase }) {
	const { t } = useLingui();
	const item = props.item;
	return (
		<article className="relative flex h-full w-[85vw] flex-col md:min-h-[398px] md:w-[586px]">
			<div className="bg-apricot absolute inset-0 rounded-3xl" />
			<div className="absolute top-0 left-0 h-[92px] w-[161px] rounded-br-3xl bg-white" />

			{/* Where the bite runs out — at the top edge and down the left one — the hand-off rounds the
			    apricot away from the tab, so those two junctions are CONVEX corners on the card rather
			    than the concave one above. What has to be painted white there is the sliver between the
			    corner and its arc: a 24px square with the quarter-disc REMOVED, the exact complement of
			    what `rounded-br-3xl` on a 24px block paints. No single border-radius can draw an inverted
			    corner like that — a box that size always paints the disc itself, which reads as a nub
			    biting INTO the card — so the sliver comes from a radial gradient instead: white
			    everywhere except a transparent disc centred on the corner the arc curves around, which
			    for both junctions is the block's own bottom-right. The 0.5px of feather is what keeps the
			    arc from aliasing into a staircase; a hard stop at 24px on the nose does not. */}
			<div className="absolute top-0 left-[161px] size-6 bg-[radial-gradient(circle_at_bottom_right,transparent_23.5px,white_24px)]" />
			<div className="absolute top-[92px] left-0 size-6 bg-[radial-gradient(circle_at_bottom_right,transparent_23.5px,white_24px)]" />

			{/* 148x79 at radius 16.7, inside a 161x92 bite — a uniform 13px channel on both open sides.
			    The mark spans the full 132px the plate leaves rather than the hand-off's 110x48, and where
			    it comes from is the other half of that — see `CaseLogo`.

			    `grayscale` is the hand-off's treatment, and `contrast-200` is what makes it read as ink
			    rather than a watermark: desaturating a mid-blue like Telefónica's or Ticketmaster's lands
			    it around 30% grey, which is the faintness the review reported. `contrast` pivots at 50%
			    grey, so it drives those marks towards black while leaving white exactly white — which is
			    the property the blend below depends on, and the reason it is not `brightness`. Brightness
			    darkens white too, and every logo would then multiply a grey rectangle onto the tab.

			    `mix-blend-multiply` rides along because a logo record may be a PNG with an opaque white
			    plate (most of `/home/partners` is), and multiply is what drops that plate against the cream
			    tab. The order matters: greyscale runs first and leaves white as white, which is exactly
			    what multiply then needs in order to erase it. Reaching for `brightness-0` to force pure
			    black would paint the whole plate solid.

			    The logo suspends on its own, inside the plate: the plate is a fixed 148x79 whatever it
			    holds, so a card that arrives before its logo does is the finished card with an empty tab,
			    and nothing below it moves when the logo lands. */}
			<div className="bg-sand absolute top-0 left-0 isolate flex h-[79px] w-[148px] items-center justify-center rounded-[17px] px-2">
				<Suspense fallback={null}>
					<CaseLogo portalId={item.portalId} company={item.company} />
				</Suspense>
			</div>

			{/* The mobile mockup shows ONE figure, and that is a fit constraint rather than a whim: two of
			    them need ~300px against the ~288 a 85vw card leaves, so the pair overflows leftwards and
			    lands on top of the tab. The amount raised is the one the hand-off drops.
			    Dropping it was not enough. Anchored by `right` alone the block is shrink-to-fit, so it
			    grows LEFTWARDS off its widest line — and one label at 157px still reached under the tab,
			    against the 135px between the notch and the inset. `left` bounds it at the notch so it can
			    only grow downwards; `md:left-auto` hands the two figures back their shrink-to-fit box. */}
			<div className="absolute top-8 right-8 left-[169px] flex justify-end gap-6 md:left-auto">
				<Figure value={item.projects} label={t`funded projects`} />
				<div className="hidden md:block">
					<Figure value={item.raised} label={t`raised`} />
				</div>
			</div>

			{/* The stat block is absolute, so this padding is what keeps the quote clear of it. 170px is the
			    mockup's own y for the first line; the mobile value only has to clear the smaller figures
			    (32 top + 40 number + 21 label). Shrink either and the label lands on the quote. */}
			<div className="relative flex h-full flex-col p-8 pt-[120px] md:pt-[170px]">
				<blockquote className="text-base leading-[1.5] font-medium tracking-[-0.02em]">{item.quote}</blockquote>

				{/* Every card in the deck is the same height (`items-stretch` on the carousel), so the name is
				    pinned to the bottom at every size and the row of signatures lines up as you scroll. */}
				{item.author && (
					<figcaption className="mt-auto pt-6 text-sm leading-[1.5] tracking-[-0.02em]">
						<span className="block font-medium">{item.author.name}</span>
						{/* The roles are long enough to wrap on a phone and were leaving one word alone on the
						    second line, which reads as a broken box rather than as a wrap. */}
						<span className="text-ink/50 block text-pretty">{item.author.role}</span>
					</figcaption>
				)}
			</div>
		</article>
	);
}

/**
 * "Nuestros casos de éxito" — a horizontally scrolling deck of partner stories.
 *
 * Built on the shared `Carousel` (native scroll-snap, so touch, momentum and the keyboard all work
 * without a library). Its own floating scroll buttons are turned off with `carousel-no-buttons`
 * because the hand-off puts the arrows in the section header instead; they drive the same scroller
 * through `scrollBy`, one card at a time.
 */
export function SuccessCases() {
	const { t } = useLingui();
	const scroller = useRef<HTMLUListElement>(null);

	const cases: SuccessCase[] = [
		{
			company: 'Telefónica',
			portalId: 'telefonica',
			projects: '+103',
			raised: '+700K€',
			quote: t`Fundación Telefónica partners with Worldcoo to channel donations and relief campaigns for a range of causes, especially in emergencies. The platform lets Telefónica employees and the general public donate, and every cent goes to organisations such as UNICEF.`,
		},
		{
			company: 'Ticketmaster',
			portalId: 'ticketmaster',
			projects: '+60',
			raised: '+500K€',
			quote: t`“Every month at Ticketmaster we commit to a solidarity project. National and international NGOs alike face social and environmental challenges affecting thousands of people around the world. We care about that, a lot. And we know you do too.”`,
			author: { name: 'Reyes Sáenz de Juano', role: t`Sales & Marketing Director at Ticketmaster Spain` },
		},
		{
			company: 'Eroski',
			portalId: 'eroski',
			projects: '75',
			raised: '+8M€',
			quote: t`“Charity Rounding Up is a highly effective programme and a great tool for our customers to act on their commitment to different social causes, in an easy way.”`,
			author: { name: 'Alejandro Martínez Berriochoa', role: t`Sustainability Director at Grupo Eroski` },
		},
		{
			company: 'Correos',
			portalId: 'correos',
			projects: '24',
			raised: '+1,3M€',
			quote: t`“The social causes Correos champions are aligned with the company's strategy and values. We work with as many NGOs as we can, and when an emergency hits we throw ourselves into it. Being flexible and trusting each other matters: we all want to make a positive impact.”`,
			author: { name: 'Elena Fernández', role: t`Deputy Director of International Relations and ESG at Correos` },
		},
	];

	const scrollBy = (direction: 1 | -1) => {
		const el = scroller.current;
		if (!el) return;
		// One card per press, measured rather than assumed: the distance between two consecutive items
		// already carries the gap, and it holds for the 586px desktop card and the 85vw mobile one alike.
		const [first, second] = [el.children[0], el.children[1]] as (HTMLElement | undefined)[];
		const step = first && second ? second.offsetLeft - first.offsetLeft : el.clientWidth;
		el.scrollBy({ left: direction * step, behavior: 'smooth' });
	};

	return (
		<section className="bg-white py-16 md:py-20">
			{/* Stacked on mobile — title, then the arrows under it on the left — and back to one row with the
			    arrows pushed right from `md`. The 40px gap and the 16px down to the deck are the mockup's. */}
			<div className="mx-auto flex max-w-[1440px] flex-col items-start gap-10 px-6 md:flex-row md:items-end md:justify-between md:gap-6 xl:px-10">
				<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Our success stories`}
				</h2>
				<div className="flex shrink-0 gap-2">
					<button
						type="button"
						onClick={() => scrollBy(-1)}
						aria-label={t`Previous story`}
						className="border-ink/40 hover:bg-ink/5 grid size-8 place-items-center rounded-full border transition-colors"
					>
						<ArrowLeft className="size-4" />
					</button>
					<button
						type="button"
						onClick={() => scrollBy(1)}
						aria-label={t`Next story`}
						className="border-ink/40 hover:bg-ink/5 grid size-8 place-items-center rounded-full border transition-colors"
					>
						<ArrowRight className="size-4" />
					</button>
				</div>
			</div>

			<Carousel ref={scroller} className="carousel-no-buttons mt-4 items-stretch gap-8 pb-2 md:mt-10 xl:px-10">
				{cases.map((item) => (
					<CarouselItem key={item.company} className="flex">
						<CaseCard item={item} />
					</CarouselItem>
				))}
			</Carousel>
		</section>
	);
}
